/**
 * The approved glossary, marked in the text.
 *
 * TERMS ARRIVE AFTER THE DOCUMENT DOES -- one request, answered once per open -- so this cannot be
 * a pure function of the document the way the column-type marks next door are. The plugin keeps its
 * own state and is told, through a meta transaction, what the vocabulary is and whether the reader
 * wants to see it.
 *
 * DECORATIONS ARE COMPUTED IN `apply`, NOT IN `decorations`. ProseMirror reads `decorations` on
 * every render; matching several hundred terms against every text node that often would be felt on
 * a long procedure. Here the set is rebuilt only when the document changes or the vocabulary does,
 * and mapped through every other transaction, which is what mapping is for.
 *
 * INLINE DECORATIONS, SO NOTHING IS WRITTEN DOWN. A mark in the schema would be a change to the
 * document -- it would enter the content digest, travel into the PDF, and need an approval. A
 * glossary mark is a reading aid about the text, not part of it, and a decoration is the thing that
 * means exactly that. It also means a term released tomorrow lights up in a document approved
 * yesterday, without the document changing.
 */
import { Extension } from "@tiptap/core";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import { Decoration, DecorationSet } from "@tiptap/pm/view";
import { findTerms, tooltipFor } from "./glossary";
import type { GlossaryTerm } from "./glossary";

export const glossaryKey = new PluginKey("parkourGlossary");

export type GlossaryState = { terms: GlossaryTerm[]; on: boolean };

/** Tell the editor what the vocabulary is, or whether to show it. Both are optional; sending one
 *  leaves the other as it was. */
export function setGlossary(editor: any, next: Partial<GlossaryState>) {
    if (!editor?.view) return;
    editor.view.dispatch(editor.view.state.tr.setMeta(glossaryKey, next));
}

function decorate(doc: any, state: GlossaryState): DecorationSet {
    if (!state.on || !state.terms.length) return DecorationSet.empty;

    const found: Decoration[] = [];
    doc.descendants((node: any, pos: number) => {
        if (!node.isText || !node.text) return true;
        for (const hit of findTerms(node.text, state.terms)) {
            found.push(Decoration.inline(pos + hit.from, pos + hit.to, {
                class: "parkour-glossary-term",
                title: tooltipFor(hit.term),
                // READ OUT, NOT ONLY SEEN. A dotted underline is invisible to somebody using a
                // screen reader, and the definition is the whole value of the mark.
                "aria-label": tooltipFor(hit.term),
            }));
        }
        return true;
    });

    return DecorationSet.create(doc, found);
}

export const GlossaryMarks = Extension.create({
    name: "glossaryMarks",

    addProseMirrorPlugins() {
        return [new Plugin({
            key: glossaryKey,

            state: {
                init: () => ({
                    terms: [] as GlossaryTerm[],
                    on: true,
                    decorations: DecorationSet.empty,
                }),

                apply(tr, value) {
                    const meta = tr.getMeta(glossaryKey);
                    const next = {
                        terms: meta?.terms ?? value.terms,
                        on: meta?.on ?? value.on,
                    };

                    // REBUILT WHEN THE WORDS OR THE VOCABULARY CHANGE; MAPPED OTHERWISE. A cursor
                    // move must not cost a pass over the whole document.
                    if (meta || tr.docChanged) {
                        return { ...next, decorations: decorate(tr.doc, next) };
                    }
                    return { ...next, decorations: value.decorations.map(tr.mapping, tr.doc) };
                },
            },

            props: {
                decorations(state) {
                    return glossaryKey.getState(state)?.decorations || DecorationSet.empty;
                },
            },
        })];
    },
});

export default GlossaryMarks;
